import React from 'react';
import { MealCard } from '@cubemeet/ui';
import { TouchableOpacity } from 'react-native';
import { buildMetaPairs, arrayify } from './itineraryUtils';
import { coalesceText, mergePayload } from './itineraryUtils';

interface Props {
  it: any;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onOpenPoi?: (payload: any) => void;
  dayDate?: string;
  onImageClick?: (images: any[], startIndex: number) => void;
}

const MealAdapter: React.FC<Props> = ({ it, isExpanded = false, onOpenPoi, dayDate, onImageClick }) => {
  // If the item has an external card (card object / card_id), merge card.base_payload with item.payload.
  // If it is a custom item (no card), prefer the custom/inline payload only and do NOT merge with card defaults.
  const mergedPayload = (it?.card && Object.keys(it.card || {}).length > 0) ? mergePayload(it) : (it?.custom_payload || it?.inline_payload || it?.payload || {});
  // Determine where mergedPayload came from so we can trace dataflow (card merge vs custom_payload vs inline)
  const mergedSource = (it?.card && Object.keys(it.card || {}).length > 0)
    ? 'mergePayload(card)'
    : (it?.custom_payload ? 'it.custom_payload' : (it?.inline_payload ? 'it.inline_payload' : (it?.payload ? 'it.payload' : 'empty')));
  // Debug: show which mergedPayload is chosen and whether custom_payload exists
  try {
    // eslint-disable-next-line no-console
    console.debug && console.debug('[MealAdapter] mergedPayload chosen for item', it?.id || it?.card?.id || it?.cardId, {
      mergedSource,
      custom_payload: it?.custom_payload,
      card_base_payload: it?.card && it.card.base_payload,
      mergedPayloadPreview: mergedPayload && (mergedPayload.metaPairs ? { metaPairsCount: Array.isArray(mergedPayload.metaPairs) ? mergedPayload.metaPairs.length : undefined } : {}),
      mergedMetaPairs: (mergedPayload && mergedPayload.metaPairs) || undefined,
    });
  } catch (e) {
    // ignore
  }
  const title = coalesceText(it?.title, it?.card?.title, mergedPayload.title) || '未命名';
  // 優先使用 it.note（行程表副本的描述），再使用 it.description（前端更新後的）
  const description = coalesceText(
    it?.note,
    it?.description,
    mergedPayload.description,
    it?.card?.description,
    mergedPayload.summary,
    mergedPayload.intro,
  );

  const hasCardObj = Boolean(it?.card && Object.keys(it.card || {}).length > 0) || Boolean(it?.card_id || it?.cardId || (it?.meta && (it.meta.cardId || it.meta.card_id)));
  const hasCardCover = Boolean(it?.card && (it.card.cover_image || (Array.isArray(it.card.image_gallery) && it.card.image_gallery.length > 0)));
  const imageUrl = hasCardCover ? (it?.card?.cover_image || mergedPayload?.cover_image ||
    (Array.isArray(it?.card?.image_gallery) && it.card.image_gallery.length ? it.card.image_gallery[0] : undefined) ||
    (Array.isArray(mergedPayload?.image_gallery) && mergedPayload.image_gallery.length ? mergedPayload.image_gallery[0] : undefined) ||
    undefined) : undefined;

  const locationText = coalesceText(
    it?.card?.location,
    mergedPayload.location,
    mergedPayload.address,
  );

  // Helper: safely extract a readable string from various shapes
  const extractString = (value: any): string | undefined => {
    if (value == null) return undefined;
    if (typeof value === 'string') return value.trim() || undefined;
    if (Array.isArray(value)) {
      const parts = value.map(v => extractString(v)).filter(Boolean) as string[];
      return parts.length ? parts.join(' ') : undefined;
    }
    if (typeof value === 'object') {
      const candidates = ['formatted_address', 'formattedAddress', 'address', 'display', 'label', 'name', 'title', 'short_name', 'description', 'locality'];
      for (const k of candidates) {
        if (value[k]) {
          const s = extractString(value[k]);
          if (s) return s;
        }
      }
      const values = Object.values(value).map(v => (typeof v === 'string' ? v : (typeof v === 'number' ? String(v) : ''))).filter(Boolean);
      if (values.length) return values.join(' ');
      return undefined;
    }
    return String(value).trim() || undefined;
  };

  const normalizeForCompare = (s?: string): string => {
    if (!s) return '';
    return s.toLowerCase().replace(/[\u2000-\u206F\u2E00-\u2E7F\p{P}\p{S}]/gu, ' ').replace(/\s+/g, ' ').trim();
  };

  // If there's no external card object (custom inline payload), avoid duplicating title/address
  // Prioritize mergedPayload.address (includes custom_payload override) over it.card.address
  const addressStr = extractString(mergedPayload?.address || it?.card?.address);
  let cleanedDescription = description ? String(description).trim() : undefined;
  try {
    if (!hasCardObj && cleanedDescription) {
      const titleNorm = normalizeForCompare(title);
      const addrNorm = normalizeForCompare(addressStr);
      const descNorm = normalizeForCompare(cleanedDescription);
      // if description equals title or address or includes title+address combinations, drop it
      if (!titleNorm && !addrNorm) {
        // nothing to compare
      } else if (descNorm === titleNorm || descNorm === addrNorm || descNorm === (titleNorm + ' ' + addrNorm) || descNorm === (addrNorm + ' ' + titleNorm) || descNorm.includes(titleNorm) && descNorm.includes(addrNorm)) {
        cleanedDescription = undefined;
      }
    }
  } catch (e) {
    cleanedDescription = description;
  }

  const merged = mergedPayload || {};
  // Prefer explicit metaPairs from custom_payload (authoritative snapshot) when present.
  // Fallback to computed buildMetaPairs which merges card/base/inline/custom.
  let metaPairs: Array<{ label: string; value: string }> = [];
  try {
    if (Array.isArray(it?.custom_payload?.metaPairs) && it.custom_payload.metaPairs.length) {
      metaPairs = it.custom_payload.metaPairs.map((p: any) => ({ label: String(p.label || ''), value: String((p.value === null || typeof p.value === 'undefined') ? '' : p.value) }));
      console.log('[MealAdapter] Using it.custom_payload.metaPairs:', {
        itemId: it?.id,
        metaPairs,
      });
    } else {
      metaPairs = buildMetaPairs(it, mergedPayload);
      console.log('[MealAdapter] Using buildMetaPairs (no it.custom_payload.metaPairs):', {
        itemId: it?.id,
        metaPairs,
      });
    }
  } catch (e) {
    metaPairs = buildMetaPairs(it, mergedPayload);
  }
  // Debug: print final metaPairs used to render MealCard
  try {
    // eslint-disable-next-line no-console
    console.debug && console.debug('[MealAdapter] final metaPairs', it?.id || it?.card?.id || it?.cardId, { custom_payload: it?.custom_payload, mergedPayloadMetaPairs: mergedPayload?.metaPairs, metaPairs });
  } catch (e) {
    // ignore
  }
  const tags = Array.isArray(it?.cardTags) ? it.cardTags : (Array.isArray(it?.card?.tags) ? it.card.tags : arrayify(mergedPayload.tags));

  // Avoid showing restaurantName when it duplicates the title (common for custom inline payloads)
  const extractStringSimple = (v: any) => (v == null ? undefined : (typeof v === 'string' ? v.trim() : String(v).trim()));
  const restaurantCandidate = extractStringSimple(merged.restaurantName || merged.restaurant);
  const restaurantName = (restaurantCandidate && normalizeForCompare(restaurantCandidate) !== normalizeForCompare(title)) ? restaurantCandidate : undefined;
  // For venue/address: prefer a readable address but hide if it matches title or restaurantName
  const venueCandidate = extractStringSimple(locationText);
  const venue = (venueCandidate && normalizeForCompare(venueCandidate) !== normalizeForCompare(title) && normalizeForCompare(venueCandidate) !== normalizeForCompare(restaurantName)) ? venueCandidate : undefined;
  // If item has no external card, hide meta-row venue to avoid duplicating the address (address will appear in the address row)
  // Also hide venue if it's the same as address to avoid duplicate display
  const effectiveVenue = hasCardObj && venue && normalizeForCompare(venue) !== normalizeForCompare(addressStr || '') ? (venue || locationText) : undefined;

  const payloadForModal = {
    title,
    imageUrl,
    address: addressStr || mergedPayload?.address || it?.card?.address,
    description: cleanedDescription || description,
    metaPairs,
    tags,
    rawDebug: { rawMergedPayload: mergedPayload },
    card: it?.card,
    // Ensure id is always available for sharing
    id: it?.card?.id || it?.meta?.cardId || it?.cardId,
    cardId: it?.card?.id || it?.meta?.cardId || it?.cardId,
  };

  const card = (
    <MealCard
      title={title}
      note={cleanedDescription || description}
      restaurantName={restaurantName}
      venue={effectiveVenue}
      cuisine={typeof (merged.cuisine || merged.category) === 'string' ? (merged.cuisine || merged.category) : undefined}
      course={typeof merged.course === 'string' ? merged.course : undefined}
      price={typeof merged.min_price === 'number' ? Number(merged.min_price) : typeof merged.price === 'number' ? Number(merged.price) : undefined}
      currency={typeof merged.currency === 'string' ? merged.currency : undefined}
      startTime={merged.start_time || merged.startTime}
      endTime={merged.end_time || merged.endTime}
      imageUrl={imageUrl}
      address={addressStr || extractString(mergedPayload?.address) || extractString(it?.card?.address)}
      phone={typeof (it?.card?.phone || mergedPayload?.phone) === 'string' ? (it?.card?.phone || mergedPayload?.phone) : undefined}
      openingHours={it?.card?.opening_hours || mergedPayload?.opening_hours}
      dayDate={dayDate}
    />
  );

  if (typeof onOpenPoi === 'function') {
    return (
      <TouchableOpacity activeOpacity={0.8} onPress={() => onOpenPoi(payloadForModal)}>
        {card}
      </TouchableOpacity>
    );
  }

  return card;
};

export default MealAdapter;
